import {
  CrawlerSettingsInputDto,
  CreateUserDto,
  SiteDto,
  SitePageDto,
  UserDto,
} from '@/config/api-generated/model'

export interface Process {
  id: string
  url: string
  pages: SitePageDto | null
  pageCount?: number
  isFinished: boolean
  isInspected?: boolean
  settings: CrawlerSettingsInputDto | null
  workers?: {
    live: string
    finished: string
  }
}

export enum MessageSeverity {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
}

export enum DownloadFormat {
  CSV = 'CSV',
  XLSX = 'XLSX',
  TXT = 'TXT',
  JSON = 'JSON',
  XML = 'XML',
  HTML = 'HTML',
}

export interface AppMessage {
  message: string
  severity: MessageSeverity
  tranlateNS?: string
  hideInterval?: number
}

export interface UserTokens {
  accessToken?: string
  refreshToken?: string
}

export type User = UserDto & UserTokens

export interface AppState {
  locale: string | null
  processes: { [key: string]: Partial<Process> } | null
  message?: AppMessage
  settings: CrawlerSettingsInputDto | null
  auth: {
    user: User | null
    isLoggedIn: boolean
    verifyEmail?: string
  }
  status: {
    loading: boolean
    navExpanded: boolean
    isSignUpVisible: boolean
    isAdvancedEnabled: boolean
  }
}

export type SignUpData = CreateUserDto & { passwordConfirm?: string }

export interface AppActions {
  setLocale: (locale: string) => void
  setLoading: (isLoading: boolean) => void
  setIsAdvancedEnabled: (isEnabled: boolean) => void
  setMessage: (message: AppMessage | undefined, hideAfter?: number) => void
  setNavExpanded: (expanded: boolean) => void
  addProcess: (process: Process) => void
  updateProcess: (processId: string, process: Partial<Process>) => void
  removeProcess: (processId: string) => void
  clearAllProcess: () => void
  setSetting: (setting: CrawlerSettingsInputDto | null) => void
  setUser: (user: User | null) => void
  setSignupVisible: (isVisible: boolean) => void
  setUserVerifyEmail: (email?: string) => void
}

export interface AppAsyncActions {
  run: (url: string, force?: boolean) => Promise<string | undefined>
  signIn: (
    email: string,
    password: string,
    onSuccess?: () => void | Promise<void>,
  ) => Promise<void>
  signUp: (userDetails: SignUpData) => Promise<void>
  fetchUser: () => Promise<void>
  refreshTokens: () => Promise<void>
  logout: (onSuccess?: () => void | Promise<void>) => Promise<void>
  deleteProcess: (processId: string) => Promise<void>
  abortProcess: (processId: string) => Promise<void>
  download: (id: string, format: DownloadFormat) => Promise<void>
  getSite: (id: string) => Promise<SiteDto | null>
  load: () => Promise<void>
}

export type AppStore = AppState & AppActions & AppAsyncActions
